import { nanoid } from 'nanoid';
import PropTypes, { Validator } from 'prop-types';
import {
  HTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
  RefAttributes,
  useCallback,
  useMemo,
  useState,
  WeakValidationMap,
} from 'react';
import AsyncSelect, { AsyncProps } from 'react-select/async';
import { GroupBase as MultiselectGroupBase, OnChangeValue, PropsValue } from 'react-select/dist/declarations/src';
import { SelectComponents } from 'react-select/dist/declarations/src/components';
import ReactSelectType from 'react-select/dist/declarations/src/Select';
import { PortalContextAndRefProps, withPortalDataAndRef } from '../PortalProvider/PortalContext';
import { optionalBoolValidator, optionalStringValidator } from '../utils/betterPropTypes';
import { forgeClassHelper } from '../utils/classes';
import FakeEvent from '../utils/fake-event';
import CustomClearIndicator from './CustomClearIndicator';
import CustomDropdownIndicator from './CustomDropdownIndicator';
import CustomInput from './CustomInput';
import CustomLoadingIndicator from './CustomLoadingIndicator';
import CustomMenuList from './CustomMenuList';
import CustomMultiValue from './CustomMultiValue';
import CustomOption from './CustomOption';
import { ReactSelectContext } from './ReactSelectContext';

export { type GroupBase as MultiselectGroupBase } from 'react-select/dist/declarations/src';

/** The data sent to and retrieved from a Multiselect
 *
 * The Multiselect default export also accepts string[], but that gets normalized
 * to StructuredOption before being sent to react-select.
 */
export type StructuredOption = {
  /** The value of the option when selected */
  value: string;
  /** Option is visible, but not selectable */
  disabled?: boolean;
  /** @deprecated Use `disabled` instead
   *
   * Option is visible, but not selectable */
  isDisabled?: boolean;
  /** @deprecated Use `label` instead
   *
   * The text to display to the user */
  text?: string;
  /** The text to display to the user */
  label?: string;
};

/** Represents the union of all allowable data types
 *
 * This type cannot not be effectively used externally given that the default
 * export has overloads preventing mixing string and StructuredOption data.
 */
export type MultiselectOption = string | StructuredOption;

/** Transforms loadingMessage received as a string to the type that react-select
 * expects
 */
export type FunctionFromString = (obj: { inputValue: string }) => ReactNode;
// if given a string, will return a func that returns the string. else, returns value provided
export const getFunctionFromString = (
  message: string | FunctionFromString | undefined
): FunctionFromString | undefined => (typeof message === 'string' && message !== undefined ? () => message : message);

export type MultiselectChangeEvent<Option extends StructuredOption, IsMulti extends boolean> = FakeEvent<
  OnChangeValue<Option, IsMulti>
>;

/** Base props common to both Multiselect and SingleSelect
 */
export interface AnySelectBaseProps<Option extends MultiselectOption, IsMulti extends boolean>
  extends Omit<
      AsyncProps<StructuredOption, IsMulti, MultiselectGroupBase<StructuredOption>>,
      | 'loadingMessage'
      | 'noOptionsMessage'
      | 'options'
      | 'value'
      | 'defaultValue'
      | 'onChange'
      | 'cacheOptions'
      | 'isMulti'
    >,
    Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'onBlur' | 'onFocus' | 'onChange'> {
  /**
   * Aria label for the input. If not used in FormField, either `aria-label`, `aria-labelledby`, or `inputId` associated with a `<label>` is required for accessibility.
   */
  'aria-label'?: string;
  /**
   * HTML ID of an element that should be used as the label for the input. Will override `aria-label` if provided.
   * If not used in FormField, either `aria-label`, `aria-labelledby`, or `inputId` associated with a `<label>` is required for accessibility.
   */
  'aria-labelledby'?: string;
  /** If truthy, loaded data will be cached. The cache will remain until `cacheOptions` changes value. */
  cacheOptions?: boolean;
  /** Adds a class to the root element of the component. */
  className?: string;
  /** Close the select menu when the user selects an option. */
  closeMenuOnSelect?: boolean;
  /** Impacts the styling of the multiselect in addition to preventing selection */
  disabled?: boolean;
  /** Disables the portal which can fix issues related to scrolling inside elements with scrolling effects */
  disablePortal?: boolean;
  /** Highlights the input when defined (specific string doesn't matter) */
  error?: string;
  /** Hides any required styling */
  hideRequiredStyles?: boolean;
  /** Adds an id to the root element of the component. To add an id to the `<input>` element, use the `inputId` prop */
  id?: string;
  /**
   * Will not be used if `options` are provided and component is being used synchronously.
   * The default set of options to show in the menu before the user starts searching.
   * When set to true, the results for `loadAsyncOptions('')` will be loaded on mount.
   */
  initialAsyncOptions?: Array<Option | MultiselectGroupBase<Option>>;
  /**
   * Used to set an id on the HTML `<input>` element.
   * If not used in `FormField`, either `aria-label`, `aria-labelledby`, or `inputId` associated with a `<label>` is required
   * for accessibility.
   */
  inputId?: string;
  /**
   * Function that returns a promise, which is the set of options to display in the menu once the promise resolves.
   * This function should use the `inputValue` typed into the input filter to filter the array of options.
   * Will not be used if `options` are provided and component is being used synchronously.
   */
  loadAsyncOptions?: (inputValue: string) => Promise<readonly (Option | MultiselectGroupBase<Option>)[]>;
  /** What to display  */
  loadingMessage?: string | FunctionFromString;
  /** Text to display when the there are no options to show in the menu (or a function returning the string to show). */
  noOptionsMessage?: FunctionFromString | string;
  /** Function that takes a fake event as an argument. This event contains a
   * target object with value (map of keys to true/false) and id properties.
   * Called when the Multiselect value changes.
   */
  onChange?: (event: MultiselectChangeEvent<StructuredOption, IsMulti>) => void;
  /**
   * Array of all options available.
   * Uses a prebuilt filter function which does a case insensitive string match using option value and text.
   * If you want to use different initial options or filtering, use `initialAsyncOptions` and `loadAsyncOptions` instead.
   * If provided, `loadAsyncOptions` and `initialAsyncOptions` props will not be used.
   */
  options?: Array<Option | MultiselectGroupBase<Option>>;
  /** Indicates whether it is required to select at least one option. */
  required?: boolean;
  /** Select the currently focused option when the user presses Tab. */
  tabSelectsValue?: boolean;
  /**
   * Determines the selected options, and does not allow them to be updated
   * internally ([controlled](https://reactjs.org/docs/forms.html#controlled-components)).
   */
  value?: PropsValue<Option>;
  /** Determines the initial set of selected options, but allows them to be
   * updated internally ([uncontrolled](https://reactjs.org/docs/uncontrolled-components.html)).
   */
  defaultValue?: PropsValue<Option>;
  /**
   * Text shown when no values are selected and the user is not typing.
   */
  placeholder?: string;
}

/** Props common to MultiselectComponent and the default export */
export interface MultiselectProps<Option extends MultiselectOption, IsMulti extends boolean>
  extends AnySelectBaseProps<Option, IsMulti> {
  /** Able to select multiple values if true */
  isMulti?: IsMulti;
}

/** The type of the forwarded ref */
export type MultiselectRef<IsMulti extends boolean> = ReactSelectType<
  StructuredOption,
  IsMulti,
  MultiselectGroupBase<StructuredOption>
>;

/**
 * Wrapper on top of react-select's Async component - https://react-select.com/async
 */
export const classes = forgeClassHelper({ name: 'multiselect' });
export const classesAsStr = forgeClassHelper({ name: 'multiselect', outputIsString: true });

export type NormalizeValuesReturn =
  | Array<StructuredOption | MultiselectGroupBase<StructuredOption>>
  | StructuredOption
  | null
  | undefined;
/** Normalizes values so that they are of type StructuredOption if they were
 * originally strings.
 *
 * Multiselect accepts either string or StructuredOption for many of its props.
 * This function normalizes the values to be of type StructuredOption.
 */
export function normalizeValues(values: readonly MultiselectOption[]): Array<StructuredOption>;
export function normalizeValues(
  values: readonly (MultiselectOption | MultiselectGroupBase<MultiselectOption>)[]
): Array<StructuredOption | MultiselectGroupBase<StructuredOption>>;
export function normalizeValues(
  values: (MultiselectOption | MultiselectGroupBase<MultiselectOption>)[] | undefined
): Array<StructuredOption | MultiselectGroupBase<StructuredOption>> | undefined;
export function normalizeValues(
  values: readonly MultiselectOption[] | MultiselectOption | null | undefined
): Array<StructuredOption> | null | undefined;
export function normalizeValues(
  values: readonly MultiselectOption[] | undefined
): Array<StructuredOption | MultiselectGroupBase<StructuredOption>> | undefined;
export function normalizeValues<
  Values extends
    | PropsValue<MultiselectOption>
    | Array<MultiselectOption | MultiselectGroupBase<MultiselectOption>>
    | undefined
>(values: Values): NormalizeValuesReturn {
  if (values === null || values === undefined) {
    return values as null | undefined;
  } else if (Array.isArray(values)) {
    return values.map((multiSelectOption) => {
      if (typeof multiSelectOption === 'string') {
        return {
          value: multiSelectOption,
          text: multiSelectOption,
          label: multiSelectOption,
        };
      } else if (isOption(multiSelectOption)) {
        return multiSelectOption;
      } else {
        return {
          ...multiSelectOption,
          options: normalizeValues((multiSelectOption as MultiselectGroupBase<MultiselectOption>).options),
        };
      }
    });
  } else if (typeof values === 'string') {
    return {
      value: values,
      text: values,
      label: values,
    };
  } else {
    return [values] as StructuredOption[];
  }
}

/** The final type of the Multiselect default export.
 *
 * Supports options of type string[] or StructuredOption[], but not an array with
 * a mixture of both.
 */
export type MultiselectType = {
  <IsMulti extends boolean = true>(
    props: MultiselectProps<string, IsMulti> & RefAttributes<MultiselectRef<IsMulti>>
  ): ReactElement;
  <IsMulti extends boolean = true>(
    props: MultiselectProps<StructuredOption, IsMulti> & RefAttributes<MultiselectRef<IsMulti>>
  ): ReactElement;
};

export type MultiselectComponentProps<Option extends MultiselectOption, IsMulti extends boolean> = MultiselectProps<
  Option,
  IsMulti
> &
  PortalContextAndRefProps<MultiselectRef<IsMulti>>;

export const isOption = <Option extends MultiselectOption>(
  optionOrGroup: Option | MultiselectGroupBase<Option>
): optionOrGroup is Option => !(optionOrGroup as MultiselectGroupBase<Option>).options;

// functions telling react-select how to translate from our option/value data structure to theirs
export const customGetOptionLabel = (option: StructuredOption): string => option.label || option.text || option.value;
export const customGetOptionValue = (option: StructuredOption): string => option.value;
export const customIsOptionDisabled = (option: StructuredOption): boolean =>
  option.isDisabled || option.disabled ? true : false;

/** permissive filter function used for non-async options
 */
export const simpleFilterOptions = (
  inputValue: string,
  optionsToFilter: readonly (StructuredOption | MultiselectGroupBase<StructuredOption>)[]
): readonly (StructuredOption | MultiselectGroupBase<StructuredOption>)[] => {
  if (inputValue) {
    const searchFor = (text: string): boolean => text.toLowerCase().includes(inputValue.toLowerCase());
    return optionsToFilter.reduce<Array<StructuredOption | MultiselectGroupBase<StructuredOption>>>(
      (filteredOptions, option) => {
        if (isOption(option)) {
          if (searchFor(customGetOptionValue(option)) || searchFor(customGetOptionLabel(option))) {
            filteredOptions.push(option);
          }
        } else if (option.label && searchFor(option.label)) {
          // The entire group matches by label
          filteredOptions.push(option);
        } else {
          // Search within the group for matching options

          // Groups can only be nested 1 level deep, so type assertion is accurate.
          const nestedOptions = simpleFilterOptions(inputValue, option.options) as StructuredOption[];
          if (nestedOptions.length > 0) {
            filteredOptions.push({ label: option.label, options: nestedOptions });
          }
        }
        return filteredOptions;
      },
      []
    );
  } else {
    return optionsToFilter;
  }
};

/** Core implementation of Multiselect.
 *
 * Accepts props and a forwarded ref.
 */
const MultiselectComponent = <Option extends MultiselectOption, IsMulti extends boolean>({
  cacheOptions = true,
  classNamePrefix,
  closeMenuOnSelect = false,
  defaultOptions,
  defaultValue: upstreamDefaultValue,
  disablePortal = false,
  forwardedRef,
  getOptionValue,
  getOptionLabel,
  hideSelectedOptions,
  initialAsyncOptions: upstreamInitialAsyncOptions,
  isDisabled,
  isOptionDisabled,
  isSearchable,
  loadOptions,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  className,
  disabled = false,
  error,
  hideRequiredStyles = false,
  isClearable = false,
  isMulti = true as IsMulti,
  loadAsyncOptions: upstreamLoadAsyncOptions,
  loadingMessage = 'Loading ...',
  noOptionsMessage = 'No options found',
  options: upstreamOptions,
  placeholder = '- Type or Select -',
  required = false,
  portalData,
  tabSelectsValue = false,
  value: upstreamValue,
  onChange,
  id,
  components = {},
  ...rest
}: MultiselectComponentProps<Option, IsMulti>): ReactElement => {
  const [instanceId] = useState(nanoid(6));

  // Clients of Multiselect can provide MultiselectOption[] for many of its props,
  // but we want to provide AsyncSelect types of StructuredOption[].
  // In order to make this work, we need to normalize the MultiselectOption[]
  // we receive to be StructuredOption[].
  //
  // This pattern is applied to all of the upstream* props.

  const defaultValue = useMemo(() => normalizeValues(upstreamDefaultValue), [upstreamDefaultValue]);
  const value = useMemo(() => normalizeValues(upstreamValue), [upstreamValue]);

  const options = useMemo(() => normalizeValues(upstreamOptions), [upstreamOptions]);
  const initialAsyncOptions = useMemo(
    () => normalizeValues(upstreamInitialAsyncOptions),
    [upstreamInitialAsyncOptions]
  );

  const loadAsyncOptions = useCallback(
    async (inputValue: string): Promise<readonly (StructuredOption | MultiselectGroupBase<StructuredOption>)[]> => {
      if (upstreamLoadAsyncOptions) {
        const upstreamAsyncOptions = await upstreamLoadAsyncOptions(inputValue);
        return normalizeValues(upstreamAsyncOptions);
      } else {
        return simpleFilterOptions(inputValue, options || []);
      }
    },
    [options, upstreamLoadAsyncOptions]
  );

  const onChangeHandler = (value: OnChangeValue<StructuredOption, IsMulti>): void => {
    if (onChange) {
      const fakeEvent = new FakeEvent({ value, id });
      onChange(fakeEvent);
    }
  };

  const menuPortalTarget = disablePortal ? undefined : portalData.portalNode;

  return (
    <ReactSelectContext.Provider value={{ required: required }}>
      <AsyncSelect<StructuredOption, IsMulti, MultiselectGroupBase<StructuredOption>>
        {...classes({
          modifiers: hideRequiredStyles ? 'required-styles-hidden' : '',
          states: {
            disabled,
            error: error === '' || error === undefined ? false : !disabled,
            required: required && !hideRequiredStyles && !disabled,
          },
          extra: className,
        })}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        cacheOptions={cacheOptions}
        classNamePrefix={classesAsStr()}
        closeMenuOnSelect={closeMenuOnSelect}
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          // Hide indicatorSeparator (the line to the left of the dropdown carrot) when isClearable is false.
          ...(!isClearable && { IndicatorSeparator: null }),
          ClearIndicator: CustomClearIndicator,
          LoadingIndicator: CustomLoadingIndicator,
          Option: CustomOption,
          MultiValue: CustomMultiValue,
          Input: CustomInput,
          MenuList: CustomMenuList,
          ...components,
        }}
        defaultValue={defaultValue}
        getOptionLabel={customGetOptionLabel}
        getOptionValue={customGetOptionValue}
        hideSelectedOptions={false}
        instanceId={instanceId}
        isClearable={isClearable}
        isMulti={isMulti}
        isOptionDisabled={customIsOptionDisabled}
        isSearchable={true}
        onChange={onChangeHandler}
        defaultOptions={options ? options : initialAsyncOptions}
        isDisabled={disabled}
        loadingMessage={getFunctionFromString(loadingMessage)}
        loadOptions={loadAsyncOptions}
        noOptionsMessage={getFunctionFromString(noOptionsMessage)}
        options={options}
        placeholder={placeholder}
        id={id}
        menuPortalTarget={menuPortalTarget}
        menuPosition={'fixed'}
        menuPlacement={'bottom'}
        value={value}
        tabSelectsValue={tabSelectsValue}
        {...rest}
        ref={forwardedRef}
      />
    </ReactSelectContext.Provider>
  );
};
/** While it's convenient to use the name MultiselectComponent to differentiate
 * it from the default export, the Components view of Chrome dev tools won't have
 * _any_ component named 'Multiselect' without setting a displayName.
 *
 * react-docgen would also name the default export `MultiselectComponent`
 * without setting this.
 */
MultiselectComponent.displayName = 'Multiselect';

const multiselectPropTypes: WeakValidationMap<
  MultiselectComponentProps<MultiselectOption, boolean> & { '...rest': unknown }
> = {
  /**
   * Aria label for the input. If not used in FormField, either `aria-label`, `aria-labelledby`, or
   * `inputId` associated with a `<label>` is required for accessibility.
   */
  'aria-label': PropTypes.string,
  /**
   * HTML ID of an element that should be used as the label for the input. Will override `aria-label` if provided.
   * If not used in FormField, either `aria-label`, `aria-labelledby`, or `inputId` associated with a `<label>` is required for accessibility.
   */
  'aria-labelledby': PropTypes.string,
  /** If truthy, loaded data will be cached. The cache will remain until `cacheOptions` changes value. */
  cacheOptions: PropTypes.bool,
  /** Adds a class to the root element of the component. */
  className: PropTypes.string,
  /** Close the select menu when the user selects an option. */
  closeMenuOnSelect: PropTypes.bool,
  /** Determines the initial set of selected options, but allows them to be
   * updated internally ([uncontrolled](https://reactjs.org/docs/uncontrolled-components.html)).
   */
  defaultValue: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        disabled: optionalBoolValidator,
        label: optionalStringValidator,
      }).isRequired,
    ]).isRequired
  ),
  /** Impacts the styling of the multiselect in addition to preventing selection */
  disabled: PropTypes.bool,
  /** Disables the portal which can fix issues related to scrolling inside elements with scrolling effects */
  disablePortal: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Hides any required styling */
  hideRequiredStyles: PropTypes.bool,
  /** Adds an id to the root element of the component. To add an id to the `<input>` element, use the `inputId` prop */
  id: PropTypes.string,
  /**
   * Will not be used if `options` are provided and component is being used synchronously.
   * The default set of options to show in the menu before the user starts searching.
   * When set to true, the results for `loadAsyncOptions('')` will be loaded on mount.
   *
   * It's important to use functions such as useMemo to not generate new option
   * values on every render. Otherwise keyboard navigation does not work as well
   * as it should.
   */
  initialAsyncOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        disabled: optionalBoolValidator,
        label: optionalStringValidator,
      }).isRequired,
      PropTypes.shape({
        label: optionalStringValidator,
        options: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string.isRequired,
            PropTypes.shape({
              value: PropTypes.string.isRequired,
              disabled: optionalBoolValidator,
              label: optionalStringValidator,
            }).isRequired,
          ]).isRequired
        ).isRequired,
      }).isRequired,
    ]).isRequired
  ),
  /**
   * Used to set an id on the HTML `<input>` element.
   * If not used in `FormField`, either `aria-label`, `aria-labelledby`, or `inputId` associated with a `<label>` is required
   * for accessibility.
   */
  inputId: PropTypes.string,
  /** Set to true to display a button for clearing current selections. */
  isClearable: PropTypes.bool,
  /**
   * Function that returns a promise, which is the set of options to display in the menu once the promise resolves.
   * This function should use the `inputValue` typed into the input filter to filter the array of options.
   * Will not be used if `options` are provided and component is being used synchronously.
   */
  loadAsyncOptions: PropTypes.func,
  /** Text to display when loading options (or a function returning the string to show). */
  loadingMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** Text to display when the there are no options to show in the menu (or a function returning the string to show). */
  noOptionsMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** Function that takes a JS event as an argument. Called when the Multiselect loses focus. */
  onBlur: PropTypes.func,
  /** Function that takes a fake event as an argument. This event contains a
   * target object with value (map of keys to true/false) and id properties.
   * Called when the Multiselect value changes. */
  onChange: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the Multiselect receives focus. */
  onFocus: PropTypes.func,
  /**
   * Array of all options available.
   * Uses a prebuilt filter function which does a case insensitive string match using option value and text.
   * If you want to use different initial options or filtering, use `initialAsyncOptions` and `loadAsyncOptions` instead.
   * If provided, `loadAsyncOptions` and `initialAsyncOptions` props will not be used.
   *
   * It's important to use functions such as useMemo to not generate new option
   * values on every render. Otherwise keyboard navigation does not work as well
   * as it should.
   */
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        disabled: optionalBoolValidator,
        label: optionalStringValidator,
      }).isRequired,
      PropTypes.shape({
        label: optionalStringValidator,
        options: PropTypes.arrayOf(
          PropTypes.oneOfType([
            PropTypes.string.isRequired,
            PropTypes.shape({
              value: PropTypes.string.isRequired,
              disabled: optionalBoolValidator,
              label: optionalStringValidator,
            }).isRequired,
          ]).isRequired
        ).isRequired,
      }).isRequired,
    ]).isRequired
  ),
  /**
   * Text shown when no values are selected and the user is not typing.
   */
  placeholder: PropTypes.string,
  /** An instance of the react-select class-based component which underlies this
   * implementation.
   */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    PropTypes.shape({
      current: PropTypes.any,
    }).isRequired,
  ]) as Validator<Ref<MultiselectRef<boolean>>>,
  /** Indicates whether it is required to select at least one option. */
  required: PropTypes.bool,
  /** Select the currently focused option when the user presses Tab. */
  tabSelectsValue: PropTypes.bool,
  /**
   * Determines the selected options, and does not allow them to be updated
   * internally ([controlled](https://reactjs.org/docs/forms.html#controlled-components)).
   */
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string.isRequired,
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          disabled: optionalBoolValidator,
          label: optionalStringValidator,
        }).isRequired,
      ]).isRequired
    ),
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        disabled: optionalBoolValidator,
        label: optionalStringValidator,
      }).isRequired,
    ]),
  ]),
  /**
   * [react-select Async component](https://react-select.com/props#replacing-components)
   * components prop. This gets swallowed and used to override the default
   * components.
   */
  components: PropTypes.shape({
    ClearIndicator: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['ClearIndicator']
    >,
    Control: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['Control']
    >,
    DropdownIndicator: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['DropdownIndicator']
    >,
    DownChevron: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['DownChevron']
    >,
    CrossIcon: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['CrossIcon']
    >,
    Group: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['Group']
    >,
    GroupHeading: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['GroupHeading']
    >,
    IndicatorsContainer: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['IndicatorsContainer']
    >,
    IndicatorSeparator: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['IndicatorSeparator']
    >,
    Input: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['Input']
    >,
    LoadingIndicator: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['LoadingIndicator']
    >,
    Menu: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['Menu']
    >,
    MenuList: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['MenuList']
    >,
    MenuPortal: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['MenuPortal']
    >,
    LoadingMessage: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['LoadingMessage']
    >,
    NoOptionsMessage: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['NoOptionsMessage']
    >,
    MultiValue: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['MultiValue']
    >,
    MultiValueContainer: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['MultiValueContainer']
    >,
    MultiValueLabel: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['MultiValueLabel']
    >,
    MultiValueRemove: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['MultiValueRemove']
    >,
    Option: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['Option']
    >,
    Placeholder: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['Placeholder']
    >,
    SelectContainer: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['SelectContainer']
    >,
    SingleValue: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['SingleValue']
    >,
    ValueContainer: PropTypes.elementType as Validator<
      SelectComponents<StructuredOption, boolean, MultiselectGroupBase<StructuredOption>>['ValueContainer']
    >,
  }),
  /**
   * Props passed down to underlying [react-select Async component](https://react-select.com/props)
   *
   * Props from react-select that get swallowed:
   * * `classNamePrefix`
   * * `components`
   * * `defaultOptions` (use `initialAsyncOptions` instead)
   * * `getOptionLabel`
   * * `getOptionValue`
   * * `hideSelectedOptions` (hardcoded `false`)
   * * `isClearable` (hardcoded `false`)
   * * `isDisabled` (use disabled instead)
   * * `isOptionDisabled`
   * * `isSearchable` (hardcoded `true`)
   * * `loadOptions` (use `loadAsyncOptions` instead)
   * * `styles`
   */
  '...rest': PropTypes.any,
};
MultiselectComponent.propTypes = multiselectPropTypes;

export default withPortalDataAndRef(MultiselectComponent) as MultiselectType;
